import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, 
  BarChart3, 
  MessageSquare, 
  Shield, 
  Search,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  UserPlus,
  Activity,
  TrendingUp
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is admin - with refresh mechanism
  useEffect(() => {
    const checkAdminAccess = async () => {
      setCheckingAuth(true);
      
      // Always try to fetch fresh user data first
      let currentUser = user;
      let userRole = user?.role;
      
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          withCredentials: true
        };
        
        if (token) {
          config.headers = {
            Authorization: `Bearer ${token}`
          };
        }
        
        const response = await axios.get(`${API}/auth/me`, config);
        console.log('AdminDashboard - Fresh user data:', response.data);
        currentUser = response.data;
        userRole = response.data?.role;
        
        // Update user in context with fresh data
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // If we can't get user data, user needs to log in
        if (error.response?.status === 401) {
          setCheckingAuth(false);
          // Clear invalid tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Don't redirect - show login prompt in the UI
          return;
        }
        // For other errors, use existing user if available
        if (!user) {
          setCheckingAuth(false);
          return;
        }
      }
      
      // Check role
      console.log('AdminDashboard - Checking role:', userRole);
      console.log('AdminDashboard - Current user:', currentUser);
      
      setCheckingAuth(false);
      
      if (userRole === 'superadmin') {
        console.log('AdminDashboard - Access granted!');
        // User is admin, allow access - don't redirect
        return;
      }
      
      // Not admin - show error but don't redirect (let them see the error)
      console.warn('AdminDashboard - Access denied. Role:', userRole);
      if (currentUser) {
        toast({
          title: "Access Denied",
          description: `You don't have permission to access this page. Your role is: ${userRole || 'not set'}. Please contact support if you believe this is an error.`,
          variant: "destructive"
        });
      }
    };
    
    // Always check when component mounts or user changes
    checkAdminAccess();
  }, [user, setUser, toast]);

  // Overview/Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Support tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketStatus, setTicketStatus] = useState('');
  const [ticketNotes, setTicketNotes] = useState('');

  // Fetch analytics
  useEffect(() => {
    if (user?.role === 'superadmin' && activeTab === 'overview') {
      fetchAnalytics();
    }
  }, [user, activeTab]);

  // Fetch users
  useEffect(() => {
    if (user?.role === 'superadmin' && activeTab === 'users') {
      fetchUsers();
    }
  }, [user, activeTab, usersPage, usersSearch]);

  // Fetch tickets
  useEffect(() => {
    if (user?.role === 'superadmin' && activeTab === 'support') {
      fetchTickets();
    }
  }, [user, activeTab, ticketsPage, ticketStatus]);

  const fetchAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data.",
        variant: "destructive"
      });
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const params = {
        page: usersPage,
        limit: 20
      };
      if (usersSearch) params.search = usersSearch;
      
      const response = await axios.get(`${API}/admin/users`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        withCredentials: true
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to load users.",
        variant: "destructive"
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoadingTickets(true);
      const params = {
        page: ticketsPage,
        limit: 20
      };
      if (ticketStatus) params.status = ticketStatus;
      
      const response = await axios.get(`${API}/admin/support/tickets`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        withCredentials: true
      });
      setTickets(response.data.tickets || []);
      setTicketsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      // Tickets might not exist yet, that's okay
      setTickets([]);
      setTicketsTotal(0);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleUserAction = async (userId, action, data = {}) => {
    try {
      setLoading(true);
      if (action === 'delete') {
        if (!window.confirm('Are you sure you want to delete this user and all their data?')) {
          return;
        }
        await axios.delete(`${API}/admin/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          withCredentials: true
        });
        toast({
          title: "Success",
          description: "User deleted successfully.",
        });
        fetchUsers();
      } else if (action === 'update') {
        await axios.put(`${API}/admin/users/${userId}`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          withCredentials: true
        });
        toast({
          title: "Success",
          description: "User updated successfully.",
        });
        fetchUsers();
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error performing user action:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to perform action.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTicketUpdate = async (ticketId) => {
    try {
      setLoading(true);
      const updateData = {};
      if (ticketStatus) updateData.status = ticketStatus;
      if (ticketNotes) updateData.admin_notes = ticketNotes;
      
      await axios.put(`${API}/admin/support/tickets/${ticketId}`, updateData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        withCredentials: true
      });
      toast({
        title: "Success",
        description: "Ticket updated successfully.",
      });
      fetchTickets();
      setSelectedTicket(null);
      setTicketStatus('');
      setTicketNotes('');
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update ticket.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        withCredentials: true
      });
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast({
        title: "Error",
        description: "Failed to load user details.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }
  
  // If no user after checking, show error
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
          <p className="text-gray-600 mb-4">
            Please log in to access the admin dashboard.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // If role is not superadmin, show access denied message
  if (user.role !== 'superadmin') {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the admin dashboard.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Your role: {user.role || 'not set'}<br/>
            Your email: {user.email}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-8 w-8" />
            Admin Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Manage users, support, and view platform analytics</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Users ({usersTotal})
            </TabsTrigger>
            <TabsTrigger value="support">
              <MessageSquare className="h-4 w-4 mr-2" />
              Support ({ticketsTotal})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            {loadingAnalytics ? (
              <div className="text-center py-12">Loading analytics...</div>
            ) : analytics ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                    <Users className="h-4 w-4 text-gray-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.users?.total || 0}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {analytics.users?.active || 0} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium">New Users (Month)</CardTitle>
                    <UserPlus className="h-4 w-4 text-gray-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.users?.new_this_month || 0}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {analytics.users?.new_this_week || 0} this week
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                    <DollarSign className="h-4 w-4 text-gray-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${(analytics.revenue?.total || 0).toFixed(2)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {analytics.revenue?.transactions || 0} transactions
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
                    <TrendingUp className="h-4 w-4 text-gray-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {analytics.subscriptions?.active || 0}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {analytics.subscriptions?.total || 0} total
                    </p>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 lg:col-span-4">
                  <CardHeader>
                    <CardTitle>Platform Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Contacts</div>
                        <div className="text-2xl font-bold">{analytics.content?.contacts || 0}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Companies</div>
                        <div className="text-2xl font-bold">{analytics.content?.companies || 0}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Deals</div>
                        <div className="text-2xl font-bold">{analytics.content?.deals || 0}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Campaigns</div>
                        <div className="text-2xl font-bold">{analytics.content?.campaigns || 0}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Tasks</div>
                        <div className="text-2xl font-bold">{analytics.content?.tasks || 0}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">No analytics data available</div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>User Management</CardTitle>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search users..."
                      value={usersSearch}
                      onChange={(e) => setUsersSearch(e.target.value)}
                      className="w-64"
                    />
                    <Button onClick={fetchUsers} variant="outline" size="sm">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingUsers ? (
                  <div className="text-center py-12">Loading users...</div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {users.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="font-medium">{u.name}</div>
                            <div className="text-sm text-gray-500">{u.email}</div>
                            <div className="text-xs text-gray-400 mt-1">
                              Role: {u.role} | 
                              Contacts: {u.contacts_count || 0} | 
                              Deals: {u.deals_count || 0} | 
                              {u.is_active ? (
                                <span className="text-green-600">Active</span>
                              ) : (
                                <span className="text-red-600">Inactive</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewUserDetails(u.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUserAction(u.id, 'update', {
                                is_active: !u.is_active
                              })}
                            >
                              {u.is_active ? (
                                <XCircle className="h-4 w-4 text-red-600" />
                              ) : (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUserAction(u.id, 'delete')}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {users.length === 0 && (
                      <div className="text-center py-12 text-gray-500">No users found</div>
                    )}
                    {usersTotal > 20 && (
                      <div className="flex items-center justify-between mt-4">
                        <Button
                          variant="outline"
                          onClick={() => setUsersPage(usersPage - 1)}
                          disabled={usersPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-gray-500">
                          Page {usersPage} of {Math.ceil(usersTotal / 20)}
                        </span>
                        <Button
                          variant="outline"
                          onClick={() => setUsersPage(usersPage + 1)}
                          disabled={usersPage >= Math.ceil(usersTotal / 20)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* User Details Modal */}
            {selectedUser && (
              <Card className="mt-4">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>User Details</CardTitle>
                    <Button variant="outline" onClick={() => setSelectedUser(null)}>
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <div className="font-medium">{selectedUser.name}</div>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <div>{selectedUser.email}</div>
                    </div>
                    <div>
                      <Label>Role</Label>
                      <select
                        value={selectedUser.role}
                        onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                        className="w-full p-2 border rounded"
                      >
                        <option value="user">User</option>
                        <option value="superadmin">Superadmin</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <Label>
                        <input
                          type="checkbox"
                          checked={selectedUser.is_active}
                          onChange={(e) => setSelectedUser({...selectedUser, is_active: e.target.checked})}
                          className="mr-2"
                        />
                        Active
                      </Label>
                      <Label>
                        <input
                          type="checkbox"
                          checked={selectedUser.is_verified}
                          onChange={(e) => setSelectedUser({...selectedUser, is_verified: e.target.checked})}
                          className="mr-2"
                        />
                        Verified
                      </Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contacts</Label>
                        <div className="font-medium">{selectedUser.contacts_count || 0}</div>
                      </div>
                      <div>
                        <Label>Deals</Label>
                        <div className="font-medium">{selectedUser.deals_count || 0}</div>
                      </div>
                      <div>
                        <Label>Companies</Label>
                        <div className="font-medium">{selectedUser.companies_count || 0}</div>
                      </div>
                      <div>
                        <Label>Campaigns</Label>
                        <div className="font-medium">{selectedUser.campaigns_count || 0}</div>
                      </div>
                    </div>
                    <div>
                      <Label>Subscription</Label>
                      <div>
                        {selectedUser.subscription ? (
                          <span className="font-medium">{selectedUser.subscription.plan_id}</span>
                        ) : (
                          <span className="text-gray-500">Free plan</span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleUserAction(selectedUser.id, 'update', {
                        role: selectedUser.role,
                        is_active: selectedUser.is_active,
                        is_verified: selectedUser.is_verified
                      })}
                      className="w-full"
                    >
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Support Tickets</CardTitle>
                  <select
                    value={ticketStatus}
                    onChange={(e) => setTicketStatus(e.target.value)}
                    className="p-2 border rounded"
                  >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTickets ? (
                  <div className="text-center py-12">Loading tickets...</div>
                ) : tickets.length > 0 ? (
                  <div className="space-y-2">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{ticket.subject}</div>
                            <div className="text-sm text-gray-500">{ticket.user_email}</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                              ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {ticket.status}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              ticket.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                              ticket.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                              ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {ticket.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">No support tickets</div>
                )}
              </CardContent>
            </Card>

            {/* Ticket Details Modal */}
            {selectedTicket && (
              <Card className="mt-4">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Ticket Details</CardTitle>
                    <Button variant="outline" onClick={() => {
                      setSelectedTicket(null);
                      setTicketStatus('');
                      setTicketNotes('');
                    }}>
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Subject</Label>
                      <div className="font-medium">{selectedTicket.subject}</div>
                    </div>
                    <div>
                      <Label>User Email</Label>
                      <div>{selectedTicket.user_email}</div>
                    </div>
                    <div>
                      <Label>Message</Label>
                      <div className="p-3 bg-gray-50 rounded">{selectedTicket.message}</div>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select
                        value={ticketStatus || selectedTicket.status}
                        onChange={(e) => setTicketStatus(e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <Label>Admin Notes</Label>
                      <textarea
                        value={ticketNotes || selectedTicket.admin_notes || ''}
                        onChange={(e) => setTicketNotes(e.target.value)}
                        className="w-full p-2 border rounded"
                        rows={4}
                        placeholder="Add admin notes..."
                      />
                    </div>
                    <Button
                      onClick={() => handleTicketUpdate(selectedTicket.id)}
                      className="w-full"
                    >
                      Update Ticket
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;

